/**
 * Chain configuration for Breakout protocol
 *
 * Contains deployed contract addresses for each supported chain.
 * For chain 31337 (Anvil), addresses are updated by scripts/e2e-setup.sh during E2E setup.
 */
import type { Address } from "viem"

/**
 * Supported chain IDs for Breakout protocol
 */
export const SUPPORTED_CHAIN_IDS = [84532, 8453, 31337] as const
export type SupportedChainId = (typeof SUPPORTED_CHAIN_IDS)[number]

/**
 * Chain configuration containing all deployed contract addresses.
 * Note: Operator, resolver, and treasury are stored in the registry and should be read on-chain.
 */
export interface ChainConfig {
	/** Human-readable chain name */
	name: string
	/** BreakoutMarketFactory contract address */
	factory: Address
	/** BreakoutMarket implementation address */
	implementation: Address
	/** BreakoutRegistry contract address - source of truth for operator, resolver, treasury */
	registry: Address
	/** USDC token address */
	usdc: Address
	/** Block explorer base URL */
	explorer: string
	/** Whether this is a testnet */
	isTestnet: boolean
}

/**
 * Chain configurations indexed by chain ID
 */
export const CHAIN_CONFIGS: Record<SupportedChainId, ChainConfig> = {
	// CREATE2 deployed 2026-01-26 v5 (liquidityParam direct passing - fixes custom odds)
	84532: {
		name: "Base Sepolia",
		factory: "0x000000f46fDE83550a2C6B768588C590eBecD41D",
		implementation: "0x0000002D1e2C02e834b89E0e0809f5663aC795F9",
		registry: "0x0000000A447f55f3885e58A8270B4E3228951b06",
		usdc: "0xb825dF45fC373425d8a93017Aff70c1a4377db55",
		explorer: "https://sepolia.basescan.org",
		isTestnet: true,
	},
	// Mainnet - Registry same as testnet, Market/Factory need new deployment with v5 bytecode
	8453: {
		name: "Base",
		factory: "0x00000000b4b4Ff74A41B7EbAedc830fa395e69fb",
		implementation: "0x0000002D1e2C02e834b89E0e0809f5663aC795F9", // Same salt as testnet (same constructor args)
		registry: "0x0000000A447f55f3885e58A8270B4E3228951b06",
		usdc: "0x833589fCD6eDb6E08f4c7C32D4f71b54bdA02913",
		explorer: "https://basescan.org",
		isTestnet: false,
	},
	// NOTE: These addresses are updated by scripts/e2e-setup.sh during E2E setup
	31337: {
		name: "Anvil (Local)",
		factory: "0xfB057830bB25995288be9438444d5E103c250A7c" as Address, // Updated by e2e-setup.sh
		implementation: "0xe55467EeB6562cf5457d3Ee79496eFE6019c2487" as Address, // Updated by e2e-setup.sh
		registry: "0x0000000000000000000000000000000000000000" as Address, // Updated by e2e-setup.sh
		usdc: "0x9eDbE67B376FA3DF4D1d80E94ae33909acaC33ab" as Address, // Updated by e2e-setup.sh
		explorer: "http://localhost:8545",
		isTestnet: true,
	},
}

/**
 * Type guard to check if a chain ID is supported
 */
export function isSupportedChainId(chainId: number): chainId is SupportedChainId {
	return SUPPORTED_CHAIN_IDS.includes(chainId as SupportedChainId)
}

/**
 * Get chain configuration by chain ID
 *
 * @throws Error if chain ID is not supported
 */
export function getChainConfig(chainId: number): ChainConfig {
	if (!isSupportedChainId(chainId)) {
		throw new Error(
			`Unsupported chain ID: ${chainId}. Supported: ${SUPPORTED_CHAIN_IDS.join(", ")}`,
		)
	}
	return CHAIN_CONFIGS[chainId]
}

/**
 * Get chain configuration, returning undefined for unsupported chains
 */
export function tryGetChainConfig(chainId: number): ChainConfig | undefined {
	if (!isSupportedChainId(chainId)) {
		return undefined
	}
	return CHAIN_CONFIGS[chainId]
}

/**
 * Get explorer URL for a contract address
 */
export function getExplorerUrl(chainId: SupportedChainId, address: Address): string {
	const config = CHAIN_CONFIGS[chainId]
	return `${config.explorer}/address/${address}`
}

/**
 * Get explorer URL for a transaction hash
 */
export function getExplorerTxUrl(chainId: SupportedChainId, txHash: string): string {
	const config = CHAIN_CONFIGS[chainId]
	return `${config.explorer}/tx/${txHash}`
}

/**
 * Validate that a chain has been properly configured (not zero addresses)
 */
export function isChainConfigured(chainId: SupportedChainId): boolean {
	const config = CHAIN_CONFIGS[chainId]
	return config.factory !== "0x0000000000000000000000000000000000000000"
}

/**
 * Get all configured (non-zero) chains
 */
export function getConfiguredChains(): SupportedChainId[] {
	return SUPPORTED_CHAIN_IDS.filter(isChainConfigured)
}
